import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, Loader2, Hammer } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

/**
 * "Log Customer Call" modal. Captures the customer-reported enquiry (never confirmed
 * work) and can create a Job from the call, prefilled with the existing customer/vehicle.
 */
export function LogCallModal({ open, onClose, customerId, vehicleId, customerName, vrm, onSaved }) {
  const navigate = useNavigate();
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF({}); }, [open]);

  const save = async (then) => {
    setSaving(true);
    try {
      const body = {
        customer_id: customerId || null, vehicle_id: vehicleId || null,
        caller: f.caller || customerName || null,
        symptoms: f.symptoms || "", warning_lights: f.warning_lights || "",
        advice_given: f.advice_given || "", outcome: f.outcome || "",
        promised_callback_date: f.promised_callback_date || null, notes: f.notes || "",
      };
      await api("/call-logs", { method: "POST", body: JSON.stringify(body) });
      toast.success("Call logged");
      onSaved?.();
      onClose?.();
      if (then === "job") navigate(`/jobs/new?${vehicleId ? `vehicle_id=${vehicleId}` : ""}${customerId ? `&customer_id=${customerId}` : ""}`);
    } catch (e) { toast.error(e.message || "Could not log call"); } finally { setSaving(false); }
  };

  const field = (k, label, ph = "") => (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold tracking-widest text-muted-foreground">{label}</span>
      <input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`call-${k}`} placeholder={ph}
        className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent data-testid="log-call-modal" className="max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Phone className="h-5 w-5 text-brand" />Log Customer Call</DialogTitle>
          <DialogDescription>{customerName || "Customer"}{vrm ? ` · ${vrm}` : ""}. Records what the customer reported — not confirmed work.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {field("caller", "CALLER (name / number)", customerName || "")}
          <label className="block">
            <span className="mb-1 block text-[11px] font-bold tracking-widest text-muted-foreground">REPORTED SYMPTOMS</span>
            <textarea value={f.symptoms || ""} onChange={(e) => setF({ ...f, symptoms: e.target.value })} rows={2} data-testid="call-symptoms"
              className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm outline-none focus:border-brand" placeholder="e.g. knocking noise front left when braking" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            {field("warning_lights", "WARNING LIGHTS", "e.g. engine, ABS")}
            {field("outcome", "OUTCOME", "booked / quote / callback")}
          </div>
          {field("advice_given", "ADVICE GIVEN", "what you told the customer")}
          <label className="block">
            <span className="mb-1 block text-[11px] font-bold tracking-widest text-muted-foreground">PROMISED CALLBACK DATE</span>
            <input type="date" value={f.promised_callback_date || ""} onChange={(e) => setF({ ...f, promised_callback_date: e.target.value })} data-testid="call-callback"
              className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm outline-none focus:border-brand" />
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] font-bold tracking-widest text-muted-foreground">NOTES</span>
            <textarea value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} data-testid="call-notes"
              className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm outline-none focus:border-brand" />
          </label>
        </div>
        <DialogFooter className="gap-2">
          <button onClick={onClose} className="rounded-lg border border-border bg-card px-4 py-2.5 text-sm font-bold text-midnight hover:bg-secondary" data-testid="call-cancel">Cancel</button>
          <button onClick={() => save()} disabled={saving} data-testid="call-save" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 text-sm font-bold text-midnight hover:bg-secondary disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Save
          </button>
          <button onClick={() => save("job")} disabled={saving} data-testid="call-save-job" className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50">
            <Hammer className="h-4 w-4" />Save &amp; Create Job
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
